import {
  Table,
  Tr,
  Th,
  Td,
  Thead,
  Tbody,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import {
  error as commonError,
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';

import {
  RailzIntegrationFragmentGQLType,
  useDisconnectRailzIntegrationMutation,
  GetActiveRailzConnectionsByOrgIdDocumentGQLType,
} from '../../requests/financialData.gql.generated';
import { RailzFetchFinancialDataModal } from './RailzFetchFinancialDataModal';
import { colors } from 'packages/common-frontend/colors';
import {
  ResetIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';

const RailzConnectionRow = ({
  railzIntegration,
}: {
  railzIntegration: RailzIntegrationFragmentGQLType;
}) => {
  const { railzServiceName } = railzIntegration;

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [
    disconnectRailzIntegration,
    { loading: isLoadingDisconnect },
  ] = useDisconnectRailzIntegrationMutation({
    refetchQueries: [GetActiveRailzConnectionsByOrgIdDocumentGQLType],
    onCompleted: () => {
      toast({
        title: `Successfully disconnected from ${commonUtils.toTitleCase(
          railzServiceName,
        )}.`,
        position: 'top',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error) => {
      Sentry.captureException(
        new commonError.FinleyError(
          `Failed to disconnect Railz integration for ${railzServiceName}`,
          { metadata: { logMeta: { error, railzIntegration } } },
        ),
      );
      toast({
        title: `An error occurred while disconnecting from ${commonUtils.toTitleCase(
          railzServiceName,
        )}. Please contact support@finleycms.com if this continues.`,
        position: 'top',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    },
  });

  return (
    <Tr>
      <Td>{commonUtils.toTitleCase(railzIntegration.railzServiceName)}</Td>
      <Td>{commonDate.epochToLocaleDateString(railzIntegration.created_at)}</Td>
      <Td>
        <ResetIconButton
          label="Fetch Data"
          ariaLabel="fetch data"
          onClick={onOpen}
        />
        <DeleteIconButton
          label="Disconnect"
          ariaLabel="disconnect"
          onClick={async () => {
            Sentry.addBreadcrumb({
              category: 'ui.click',
              message: 'Clicked disconnect Railz integration',
              level: Sentry.Severity.Info,
            });
            await disconnectRailzIntegration({
              variables: { railzIntegrationId: railzIntegration.id },
            });
          }}
          isLoading={isLoadingDisconnect}
        />
      </Td>

      <RailzFetchFinancialDataModal
        isOpen={isOpen}
        onClose={onClose}
        railzIntegration={railzIntegration}
      />
    </Tr>
  );
};

export const RailzConnectionTable = ({
  connections,
}: {
  connections: RailzIntegrationFragmentGQLType[];
}) => {
  return (
    <Table fontSize="sm" variant="simple" w="fit-content">
      <Thead background={colors.tableHeader} borderBottom="1px solid lightgrey">
        <Tr>
          <Th w="sm">Service</Th>
          <Th w="xs">Created At</Th>
          <Th w="15" />
        </Tr>
      </Thead>
      <Tbody>
        {connections.map((railzIntegration) => (
          <RailzConnectionRow
            key={railzIntegration.id}
            railzIntegration={railzIntegration}
          />
        ))}
      </Tbody>
    </Table>
  );
};
